import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { ChevronRight, Eye, PackageCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import type { DataTableColumn, TableAction } from '@/shared/ui/data-table';
import type { ReceiveTableRow } from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import { cn } from '@/lib/utils';

export interface ReceiveColumnHandlers {
  onReceive?: (row: ReceiveTableRow) => void;
  onViewDetails?: (row: ReceiveTableRow) => void;
}

export interface ReceiveRowActionOptions {
  /**
   * Whether the user may record a receipt. `confirm-arrival` requires an
   * operator role and answers `403` for read-only users, so the action is hidden
   * rather than offered and then refused.
   */
  canReceive?: boolean;
}

const DASH = '—';

/** Placeholder children stand in for a lazy fetch that is pending, empty or failed. */
const PLACEHOLDER_ROW_TYPES: ReceiveTableRow['rowType'][] = ['loading', 'empty', 'error'];

const isPlaceholderRow = (row: ReceiveTableRow): boolean => PLACEHOLDER_ROW_TYPES.includes(row.rowType);

/** Indent grows with depth; balance lines sit one level below their consignment. */
const indentClass = (level: number): string => cn(level === 1 && 'pl-6', level === 2 && 'pl-12', level >= 3 && 'pl-16');

const renderPlaceholder = (row: ReceiveTableRow, t: TFunction): ReactNode => {
  if (row.rowType === 'loading') {
    return (
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" />
        {t('common.loading', 'Loading...')}
      </span>
    );
  }
  if (row.rowType === 'error') {
    return (
      <span className="text-sm text-destructive">
        {t('receive.dashboard.error.loadChildrenFailed', 'Failed to load. Collapse and try again.')}
      </span>
    );
  }
  return <span className="text-sm text-muted-foreground">{t('common.noData', 'No data available')}</span>;
};

export const getReceiveColumns = (t: TFunction): DataTableColumn<ReceiveTableRow>[] => [
  {
    id: 'group',
    header: t('request.destination.label', 'Destination'),
    width: 'min-w-[260px] max-w-[420px] whitespace-normal',
    // Content is gated by `rowType`, not level, so one column set serves both the
    // flat (location → consignment → balance) and grouped (location → date →
    // consignment → balance) hierarchies.
    cell: (row) => {
      if (isPlaceholderRow(row)) return renderPlaceholder(row, t);

      if (row.rowType === 'location') {
        return (
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{row.locationLabel}</span>
              {row.consignmentCount != null && (
                <Badge variant="secondary">
                  {t('receive.dashboard.subtitle', '{{count}} Consignments', { count: row.consignmentCount })}
                </Badge>
              )}
            </span>
            {row.sourceLocation && (
              <span className="text-xs text-muted-foreground">
                {t('receive.dashboard.fromLocation', 'from {{location}}', { location: row.sourceLocation })}
              </span>
            )}
          </span>
        );
      }

      if (row.rowType === 'date') {
        return (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{formatDisplayDate(row.date)}</span>
            {row.consignmentCount != null && (
              <Badge variant="secondary">
                {t('receive.dashboard.subtitle', '{{count}} Consignments', { count: row.consignmentCount })}
              </Badge>
            )}
          </span>
        );
      }

      if (row.rowType === 'balance') {
        return (
          <span className="flex flex-col">
            <span className="text-sm text-foreground">
              {row.lineNumber != null && (
                <span className="mr-2 text-muted-foreground tabular-nums">{row.lineNumber}</span>
              )}
              {row.productDescription || DASH}
            </span>
            {row.ownerItemDescription && (
              <span className="text-xs text-muted-foreground">{row.ownerItemDescription}</span>
            )}
          </span>
        );
      }

      return null;
    },
    className: (row: ReceiveTableRow) => indentClass(row.level)
  },
  {
    id: 'consignmentNumber',
    header: t('table.consignmentNumber', 'Consignment Number'),
    width: 'min-w-[150px]',
    cell: (row) =>
      row.rowType !== 'consignment' ? null : row.consignmentNumber ? (
        <Badge variant="outline">{row.consignmentNumber}</Badge>
      ) : (
        <span className="text-muted-foreground">{DASH}</span>
      )
  },
  {
    id: 'vehicle',
    header: t('table.vehicle', 'Vehicle'),
    width: 'min-w-[110px]',
    cell: (row) => (row.rowType === 'consignment' ? (row.truckNumber ?? DASH) : null)
  },
  {
    id: 'dispatched',
    header: t('table.dispatched', 'Dispatched'),
    width: 'min-w-[110px]',
    cell: (row) =>
      row.rowType === 'consignment' ? (
        <span className="text-sm text-muted-foreground">{formatDisplayDate(row.dispatchDate)}</span>
      ) : null
  },
  {
    id: 'waybill',
    header: t('table.waybill', 'Waybill'),
    width: 'min-w-[110px]',
    cell: (row) =>
      row.rowType === 'consignment' ? <span className="font-mono text-sm">{row.waybill || DASH}</span> : null
  },
  {
    id: 'sender',
    header: t('table.sender', 'Sender'),
    width: 'min-w-[120px] max-w-[200px] whitespace-normal',
    cell: (row) =>
      row.rowType === 'consignment' ? (
        <span title={row.organizationDescription ?? undefined}>{row.organizationAbbreviation || DASH}</span>
      ) : null
  },
  // Balance-line columns. They get their own headers rather than reusing the
  // consignment ones: a quantity rendered under "Dispatched" would make the
  // header lie. Empty on every other row type, exactly like the columns above.
  {
    id: 'unitType',
    header: t('tracking.servicereq.unittype.label', 'Unit Type'),
    width: 'min-w-[100px]',
    cell: (row) => (row.rowType === 'balance' ? <span className="text-sm">{row.unitType || DASH}</span> : null)
  },
  {
    id: 'incomingBalance',
    header: t('receive.dashboard.balances.incoming', 'Incoming'),
    width: 'min-w-[100px]',
    cell: (row) =>
      row.rowType === 'balance' ? <span className="text-sm tabular-nums">{row.incomingBalance ?? 0}</span> : null
  },
  {
    id: 'damagedBalance',
    header: t('dispatch.damaged.cond.short', 'Damaged'),
    width: 'min-w-[100px]',
    cell: (row) =>
      row.rowType === 'balance' ? <span className="text-sm tabular-nums">{row.damagedBalance ?? 0}</span> : null
  },
  {
    id: 'lostBalance',
    header: t('welcome.chart.lost.label', 'Lost'),
    width: 'min-w-[100px]',
    cell: (row) =>
      row.rowType === 'balance' ? <span className="text-sm tabular-nums">{row.lostBalance ?? 0}</span> : null
  }
];

/**
 * Row actions — rendered and pinned by the shared `DataTable` itself (via its
 * `actions` prop → `RowActions`). Gated to consignment rows via `rowType`, so
 * location, date, balance and placeholder rows get an empty actions cell.
 */
export const getReceiveRowActions = (
  t: TFunction,
  handlers: ReceiveColumnHandlers = {},
  { canReceive = true }: ReceiveRowActionOptions = {}
): TableAction<ReceiveTableRow>[] => [
  {
    id: 'receive',
    label: t('receive.dashboard.actions.receive', 'Receive'),
    icon: PackageCheck,
    isHidden: (row) => !canReceive || row.rowType !== 'consignment',
    onClick: (row) => handlers.onReceive?.(row)
  },
  {
    id: 'viewDetails',
    label: t('receive.dashboard.actions.viewDetails', 'View details'),
    icon: Eye,
    isHidden: (row) => row.rowType !== 'consignment',
    onClick: (row) => handlers.onViewDetails?.(row)
  }
];

/**
 * A row is expandable when the backend says it holds consignments, or when it is
 * a consignment (whose balance lines are only knowable after fetching). The
 * shared table asks this instead of counting loaded children, which is what makes
 * a not-yet-fetched row openable at all.
 */
export const getReceiveRowHasChildren = (row: ReceiveTableRow): boolean => Boolean(row.hasLazyChildren);

export const renderReceiveExpandIcon = (row: ReceiveTableRow, isExpanded: boolean): ReactNode => {
  if (!getReceiveRowHasChildren(row)) return null;

  return (
    <ChevronRight
      className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', isExpanded && 'rotate-90')}
    />
  );
};
